import { createContext, useContext, useEffect, useMemo } from 'react';
import { CheckoutProviderT } from './types';
import { useGetMe } from 'legacy/user/hooks';
import { CheckoutSteps } from '../types';
import useCheckoutSession from './hooks/useCheckoutSession';
import usePaymentMethod from 'hooks/usePaymentMethod';
import useCart from './hooks/useCart';
import useSpecialCode from './hooks/useSpecialCode';

const CheckoutContext = createContext({} as CheckoutProviderT);

function CheckoutProvider({
  children,
  products,
  goToStep,
  currentStep,
  organization,
  userMemberships
}) {
  const paymentMethod = usePaymentMethod({
    returnUrl: window.location.href
  });
  const { data: user, isLoading: isLoadingUser } = useGetMe();
  // const checkoutTimer = useCheckoutTimer();
  const cart = useCart();
  const specialCode = useSpecialCode();
  const checkoutSession = useCheckoutSession(
    organization,
    cart,
    paymentMethod,
    false
    // checkoutTimer.isExpired
  );

  // Filter out products that are not visible
  const filteredProducts = useMemo(
    () => products.filter((product) => product.product.isVisible),
    [products]
  );

  const isLoading = Boolean(
    checkoutSession.isStartingCheckout ||
      isLoadingUser ||
      checkoutSession.isUpdatingCheckout
  );

  const appliedDiscount = useMemo(
    () => ({
      data: checkoutSession.sessionData?.discountCode || null,
      isLoading: checkoutSession.isLoadingCheckout
    }),
    [
      checkoutSession.sessionData?.discountCode,
      checkoutSession.isLoadingCheckout
    ]
  );

  /**
   * Applies a discount code and updates the checkout session.
   */
  const applyDiscountCodeAndSave = (code: string) => {
    specialCode.discountCode.applyDiscountCode(
      code,
      (discountCode) =>
        checkoutSession.updateCheckout({
          updatedDiscountCode: discountCode
        }) as Promise<any>
    );
  };

  /**
   * Starts a checkout session
   * This happens on mount and when the checkout timer expires and user restarts the checkout process.
   */
  useEffect(() => {
    if (!organization?.id) {
      // if (!organization?.id || checkoutTimer.isExpired) {
      return;
    }

    checkoutSession.startCheckout();
    cart.reset();
    goToStep(CheckoutSteps.ProductsList);
  }, [organization?.id]);

  /**
   * Executes when a checkout session is successfully created.
   * This function applies an initial credit card.
   */
  useEffect(() => {
    if (
      checkoutSession.sessionData &&
      !checkoutSession.isStartingCheckout &&
      filteredProducts.length > 0
    ) {
      if (user?.defaultCard) {
        paymentMethod.selectedCard.setSelectedCard(user.defaultCard);
      }
    }
  }, [checkoutSession.isStartingCheckout, filteredProducts?.length]);

  return (
    <CheckoutContext.Provider
      value={{
        products: filteredProducts,
        goToStep,
        currentStep,
        organization,
        user,
        // checkoutTimer,
        cart,
        paymentMethod,
        isLoading,
        checkoutSession,
        userMemberships,
        specialCode,
        appliedDiscount,
        applyDiscountCodeAndSave
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
}

export const useCheckout = () => useContext(CheckoutContext);

export default CheckoutProvider;
